import { useMemo, useState } from "react";

import { humanise } from "../lib/format";
import { isUnsourced, isUnverified, needsAttention } from "../lib/provenance";
import type { Confidence, Finding } from "../lib/types";
import { Empty, StatusBadge } from "./primitives";

const CONFIDENCE_FILL: Record<Confidence, string> = {
  high: "bg-conf-high",
  medium: "bg-conf-medium",
  low: "bg-conf-low",
};

/**
 * The evidence ledger, which is the part a client's investment committee will
 * actually interrogate. Everything that matters is visible without expanding:
 * the claim, its confidence, and whether its sources were genuinely retrieved.
 */
export function FindingsTable({
  findings,
  problemsOnly: startWithProblems = false,
}: {
  findings: Finding[];
  /** Arrive filtered — the run header's "unsourced or unverified" count opens
   * the findings it counted (#124). The reader can still clear it. */
  problemsOnly?: boolean;
}) {
  const [category, setCategory] = useState<string>("all");
  const [confidence, setConfidence] = useState<string>("all");
  const [problemsOnly, setProblemsOnly] = useState(startWithProblems);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const categories = useMemo(
    () => Array.from(new Set(findings.map((f) => f.category))),
    [findings],
  );

  const rows = findings.filter(
    (f) =>
      (category === "all" || f.category === category) &&
      (confidence === "all" || f.confidence === confidence) &&
      (!problemsOnly || needsAttention(f)),
  );

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (findings.length === 0) {
    return (
      <Empty
        title="No findings recorded yet"
        hint="The agent records evidence during the research phase, before it writes anything."
      />
    );
  }

  return (
    <div>
      {/* Filters sit in one row above the data. */}
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5 text-sm">
        <select
          className="field w-auto py-1.5"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          aria-label="Filter by workstream"
        >
          <option value="all">All workstreams</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {humanise(c)}
            </option>
          ))}
        </select>
        <select
          className="field w-auto py-1.5"
          value={confidence}
          onChange={(e) => setConfidence(e.target.value)}
          aria-label="Filter by confidence"
        >
          <option value="all">All confidence</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-ink-2">
          <input
            type="checkbox"
            checked={problemsOnly}
            onChange={(e) => setProblemsOnly(e.target.checked)}
            className="size-4 accent-[var(--accent)]"
          />
          Needs attention
        </label>
        <span className="tabular ml-auto text-xs text-muted">
          {rows.length} of {findings.length}
        </span>
      </div>

      {rows.length === 0 ? (
        <Empty title="No findings match these filters" />
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((finding) => {
            const isOpen = expanded.has(finding.id);
            const unverified = finding.sources.filter(isUnverified);
            return (
              <li key={finding.id}>
                <button
                  onClick={() => toggle(finding.id)}
                  className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-surface-2"
                  aria-expanded={isOpen}
                >
                  <span
                    className={`mt-1.5 size-2.5 shrink-0 rounded-sm ${CONFIDENCE_FILL[finding.confidence]}`}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-baseline gap-2">
                      <span className="tabular text-xs font-semibold text-muted">
                        F{finding.id}
                      </span>
                      <span className="text-xs text-muted">
                        {humanise(finding.category)} · {finding.confidence}
                      </span>
                      {/* A dot and a word: as text, serious read 2.64:1 and
                          looked like the brand orange it is not. */}
                      {isUnsourced(finding) && (
                        <StatusBadge tone="critical">no sources</StatusBadge>
                      )}
                      {unverified.length > 0 && (
                        <StatusBadge tone="serious">{unverified.length} unverified</StatusBadge>
                      )}
                    </span>
                    <span className="mt-0.5 block text-sm">{finding.claim}</span>
                  </span>
                  <span className="mt-1 shrink-0 text-xs text-muted" aria-hidden>
                    {isOpen ? "−" : "+"}
                  </span>
                </button>

                {isOpen && (
                  <div className="space-y-2.5 border-t border-line bg-surface-2 px-4 py-3 pl-11 text-sm">
                    <p>
                      <span className="font-medium">So what — </span>
                      {finding.so_what}
                    </p>
                    {finding.quantification && (
                      <p>
                        <span className="font-medium">Quantification — </span>
                        {finding.quantification}
                      </p>
                    )}
                    {finding.caveats && (
                      <p>
                        <span className="font-medium">Caveats — </span>
                        {finding.caveats}
                      </p>
                    )}
                    <div>
                      <p className="font-medium">Sources</p>
                      {finding.sources.length === 0 ? (
                        <p className="text-muted">None recorded.</p>
                      ) : (
                        <ul className="mt-1 space-y-1">
                          {finding.sources.map((source, i) => (
                            <li key={i} className="flex flex-wrap items-baseline gap-2">
                              {source.url ? (
                                <a
                                  href={source.url}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                  className="text-accent underline underline-offset-2"
                                >
                                  {source.title}
                                </a>
                              ) : (
                                <span>{source.title}</span>
                              )}
                              <span className="text-xs text-muted">{source.kind}</span>
                              {source.published && (
                                <span className="text-xs text-muted">
                                  {source.published}
                                </span>
                              )}
                              <StatusBadge tone={source.verified ? "good" : "serious"}>
                                {source.verified ? "verified" : "unverified"}
                              </StatusBadge>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <p className="text-xs text-muted">
                      Recorded by {finding.recorded_by} · {finding.recorded_at}
                    </p>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
